/**
 * How to trace any fact on the site to the record it came from, and how to
 * cite a page or a fact. Written for the journalist who will not print a
 * number they cannot check, and for anyone who wants to.
 */
import { COPY } from '../../copy.js';
import type { DocPage } from '../types.js';

export const verifyPage: DocPage = {
  slug: 'verify',
  title: 'Verifying and citing a fact',
  audience: 'journalists',
  promise: 'How every fact names its record, how to open that record, what amended and website-sourced facts mean, how to report an error, and how to cite.',
  lead: 'Every fact on the site carries its source, the record it came from, and the period it is for. This page says how to follow that to the record itself, what the words beside a fact mean, and how to cite what you find.',
  sections: [
    {
      id: 'record',
      heading: 'Every fact names its record',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'A grant row says which return reported it: the filer, the return type, the tax period, and the IRS object identifier of the return, which is the number the IRS assigns to one e-filed document. A filing row says the same about itself. A fact read from a funder’s website names the page and the date it was captured; a federal award names the award record. The object identifier is the key to everything: it names one document, and the same document is what the IRS published, what the project read, and what the link beside the fact opens.',
          ],
        },
      ],
    },
    {
      id: 'open',
      heading: 'Opening the return',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'Beside every return and every grant is a link to the public copy of that return at ProPublica’s Nonprofit Explorer, which renders the same e-filed document the IRS published. The link is built from the filer’s EIN and the object identifier, so it opens the exact return, not the organization’s latest. For a grant, find the row on Schedule I (a Form 990) or in Part XV (a Form 990-PF): the recipient’s name there is what the site read, and the amount and purpose are as the filer wrote them. If ProPublica has not yet rendered a recent return, the IRS’s own monthly archive holds it under the same object identifier.',
          ],
        },
      ],
    },
    {
      id: 'words',
      heading: 'What the words beside a fact mean',
      blocks: [
        {
          kind: 'list',
          items: [
            ['“amended” and “replaced by a later return for this period”, on a filing row: a filer that amends a return files a second one for the same period. Both are kept; the later is current, the earlier is marked as replaced, and totals count the current one, so a grant repeated on the amendment counts once.'],
            ['“resolved: exact name match”, “resolved: name and place rule”, or “resolved: nearest name, corroborated”, on a grant row: the funder wrote a name, not a number, and the site matched it to this organization by the tier named. ', { link: '/about/resolution#reading', text: 'What a resolved link claims' }, ' says what to check.'],
            ['No badge on a linked grant row: the funder wrote this organization’s EIN on the row, and no matching was done.'],
            ['A name without a link, or “no page: no return on record” beside a name: the funder wrote a name the site could not place, or placed on an organization that has no page. It is shown as written and counts toward nothing but that funder’s list.'],
            ['“confirmed by a later filing”, on a grant read from a funder’s website: a later return reported the same grant for the same funder, recipient, and period. The row carries both citations; when the amounts differ the badge says so.'],
            ['“intermediary”: a funder that gives to more than a thousand distinct organizations, shown everywhere and set aside where funders are counted.'],
          ],
        },
      ],
    },
    {
      id: 'periods',
      heading: 'Periods and dates',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'A tax period is the month an organization’s fiscal year ended, so a grant “for August 2025” was paid during the year that ended that month. The site’s build date, on the home page and the about page, is the date of the facts: what the IRS had released when the graph was built. Between a fiscal year’s end and its return appearing here, expect a year or more; a recent year is always incomplete, and an absence is not evidence that no grant was made.',
          ],
        },
      ],
    },
    {
      id: 'errors',
      heading: 'Reporting an error',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'A fact that looks wrong is either wrong in the record or wrong in how the site read it; the link tells you which. Either way, write to ',
            { link: `mailto:${COPY.site.contact}`, text: COPY.site.contact },
            ' with the page, the fact, and what the record says. A misread is fixed in the next build and logged; a record that is wrong stays as filed, because the facts are what was filed, and the correspondence is kept with the fact.',
          ],
        },
      ],
    },
    {
      id: 'citing',
      heading: 'Citing a page or a fact',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'Cite the page by its address and the build date it showed, and a fact by the record behind it. The address of an organization’s page is its EIN; the address of a map or a subject is on the page. A citation a reader can check looks like this:',
          ],
        },
        {
          kind: 'note',
          text: ['FunderGraph, “[organization name],” fundergraph.org/org/[EIN], build of [date shown on the page]. Grant from [funder] to [recipient], [amount], tax period [month year], [Form 990 Schedule I or Form 990-PF Part XV], IRS object identifier [as shown on the row].'],
        },
        {
          kind: 'paragraph',
          text: [
            'The filed facts are public records and need no permission to quote. The site’s own computed facts, a resolution link, a map, a search ranking, are offered under CC BY 4.0, as ',
            { link: '/about/terms#derived', text: 'the terms page' },
            ' says; cite FunderGraph and the page.',
          ],
        },
      ],
    },
  ],
};
